import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ShoppingCart, LayoutGrid, Search, Star, Sparkles, List, Image as ImageIcon, Eye, X } from "lucide-react";

type ViewMode = "grid" | "list" | "thumbnail";
type SortMode = "featured" | "popular" | "newest" | "az" | "price-asc" | "price-desc";
import { PageShell, PageHero } from "@/components/site/page-shell";
import { CTABanner } from "@/components/sections/cta-banner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { cartStore } from "@/lib/cart";
import { toast } from "sonner";
import {
  fetchDigitalCategories,
  fetchDigitalItems,
  formatItemPrice,
  type DigitalItem,
} from "@/lib/digital-marketplace";
import { CategoryIcon } from "@/components/marketplace/category-icon";

export const Route = createFileRoute("/marketplace")({
  component: MarketplacePage,
  head: () => ({
    meta: [
      { title: "Marketplace — Digital products, instantly delivered — CoderLity" },
      {
        name: "description",
        content:
          "Browse digital products by category: reviews, listings, SEO, backlinks, GMB, templates, scripts, plugins and more.",
      },
      { property: "og:title", content: "Marketplace — CoderLity" },
      {
        property: "og:description",
        content: "Digital products by category — add to cart and check out instantly.",
      },
    ],
    links: [{ rel: "canonical", href: "https://coderlity.com/marketplace" }],
  }),
});

function MarketplacePage() {
  const [activeSlug, setActiveSlug] = useState<string>("all");
  const [query, setQuery] = useState("");
  const [view, setView] = useState<ViewMode>("grid");
  const [sort, setSort] = useState<SortMode>("featured");
  const [quickView, setQuickView] = useState<DigitalItem | null>(null);



  const { data: categories = [] } = useQuery({
    queryKey: ["digital-marketplace", "categories"],
    queryFn: () => fetchDigitalCategories(),
  });
  const { data: items = [], isLoading } = useQuery({
    queryKey: ["digital-marketplace", "items"],
    queryFn: () => fetchDigitalItems(),
  });

  const counts = useMemo(() => {
    const m = new Map<string, number>();
    for (const it of items) {
      if (!it.category_id) continue;
      m.set(it.category_id, (m.get(it.category_id) ?? 0) + 1);
    }
    return m;
  }, [items]);

  const activeCategory = categories.find((c) => c.slug === activeSlug) || null;

  const filtered = useMemo(() => {
    let list = items;
    if (activeCategory) list = list.filter((it) => it.category_id === activeCategory.id);
    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (it) =>
          it.name.toLowerCase().includes(q) ||
          (it.short_description ?? "").toLowerCase().includes(q),
      );
    }
    const sorted = [...list];
    switch (sort) {
      case "popular":
        sorted.sort((a, b) => {
          const ap = (Number(a.rating_avg) || 0) * Math.log1p(a.rating_count || 0);
          const bp = (Number(b.rating_avg) || 0) * Math.log1p(b.rating_count || 0);
          if (bp !== ap) return bp - ap;
          return (b.rating_count || 0) - (a.rating_count || 0);
        });
        break;
      case "newest":
        sorted.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
        break;
      case "az":
        sorted.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case "price-asc":
        sorted.sort((a, b) => (a.price_cents || 0) - (b.price_cents || 0));
        break;
      case "price-desc":
        sorted.sort((a, b) => (b.price_cents || 0) - (a.price_cents || 0));
        break;
      case "featured":
      default:
        sorted.sort((a, b) => {
          if (a.featured !== b.featured) return a.featured ? -1 : 1;
          if ((a.sort_order ?? 0) !== (b.sort_order ?? 0)) return (a.sort_order ?? 0) - (b.sort_order ?? 0);
          return a.name.localeCompare(b.name);
        });
    }
    return sorted;
  }, [items, activeCategory, query, sort]);


  return (
    <PageShell>
      <PageHero
        eyebrow="Marketplace"
        title="Digital products, instantly delivered"
        sub="Reviews, listings, SEO, backlinks, GMB, templates, scripts and more — by category."
      />
      <section className="pt-4 pb-16 sm:pt-6">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <div className="grid gap-6 lg:grid-cols-[240px_1fr]">
            {/* Mobile: horizontal scrolling category chips */}
            <div className="lg:hidden -mx-4 px-4">
              <div className="flex gap-2 overflow-x-auto pb-2 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                <button
                  onClick={() => setActiveSlug("all")}
                  className={cn(
                    "inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition",
                    activeSlug === "all"
                      ? "border-transparent bg-gradient-brand text-white shadow-glow"
                      : "border-border bg-card text-foreground hover:bg-secondary",
                  )}
                >
                  <LayoutGrid className="h-3.5 w-3.5" /> All
                  <span className="ml-1 opacity-70">{items.length}</span>
                </button>
                {categories.map((c) => {
                  const active = activeSlug === c.slug;
                  return (
                    <button
                      key={c.id}
                      onClick={() => setActiveSlug(c.slug)}
                      className={cn(
                        "inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition",
                        active
                          ? "border-transparent bg-gradient-brand text-white shadow-glow"
                          : "border-border bg-card text-foreground hover:bg-secondary",
                      )}
                    >
                      <CategoryIcon name={c.icon} className="h-3.5 w-3.5" />
                      <span className="whitespace-nowrap">{c.name}</span>
                      <span className="ml-1 opacity-70">{counts.get(c.id) ?? 0}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Desktop sidebar */}
            <aside className="hidden lg:block lg:sticky lg:top-24 lg:self-start lg:max-h-[calc(100vh-7rem)]">
              <div className="flex max-h-[calc(100vh-7rem)] flex-col rounded-2xl border border-border bg-card/60 p-3">
                <div className="px-2 pb-2 pt-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                  Categories
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto pr-1 [scrollbar-width:thin]">
                  <button
                    onClick={() => setActiveSlug("all")}
                    className={cn(
                      "flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm font-medium transition",
                      activeSlug === "all"
                        ? "bg-gradient-brand text-white shadow-glow"
                        : "text-foreground hover:bg-secondary",
                    )}
                  >
                    <span className="flex items-center gap-2">
                      <LayoutGrid className="h-4 w-4" /> All
                    </span>
                    <span className="text-xs opacity-80">{items.length}</span>
                  </button>
                  <div className="mt-1 space-y-0.5">
                    {categories.map((c) => {
                      const active = activeSlug === c.slug;
                      return (
                        <button
                          key={c.id}
                          onClick={() => setActiveSlug(c.slug)}
                          className={cn(
                            "flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm transition",
                            active
                              ? "bg-gradient-brand text-white shadow-glow"
                              : "text-foreground hover:bg-secondary",
                          )}
                        >
                          <span className="flex min-w-0 items-center gap-2">
                            <CategoryIcon name={c.icon} className="h-4 w-4 shrink-0" />
                            <span className="truncate">{c.name}</span>
                          </span>
                          <span className="text-xs opacity-70">{counts.get(c.id) ?? 0}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </aside>

            {/* Main */}
            <div>
              <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
                <div className="text-sm text-muted-foreground">
                  Showing <span className="font-semibold text-foreground">{filtered.length}</span>{" "}
                  products
                  {activeCategory && (
                    <>
                      {" "}
                      in <span className="font-semibold text-foreground">{activeCategory.name}</span>
                    </>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative w-full sm:w-64">
                    <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder="Search products…"
                      className="h-9 pl-8 text-sm"
                    />
                  </div>
                  <label className="sr-only" htmlFor="mp-sort">Sort</label>
                  <select
                    id="mp-sort"
                    value={sort}
                    onChange={(e) => setSort(e.target.value as SortMode)}
                    className="h-9 rounded-lg border border-border bg-card px-2 text-xs font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="featured">Featured</option>
                    <option value="popular">Popular</option>
                    <option value="newest">Newest</option>
                    <option value="az">A – Z</option>
                    <option value="price-asc">Price: Low → High</option>
                    <option value="price-desc">Price: High → Low</option>
                  </select>
                  <div className="flex items-center rounded-lg border border-border bg-card p-0.5">
                    {([
                      { id: "grid", icon: LayoutGrid, label: "Grid" },
                      { id: "list", icon: List, label: "List" },
                      { id: "thumbnail", icon: ImageIcon, label: "Thumbnails" },
                    ] as const).map(({ id, icon: Icon, label }) => (
                      <button
                        key={id}
                        type="button"
                        onClick={() => setView(id)}
                        aria-label={label}
                        title={label}
                        className={cn(
                          "flex h-8 w-8 items-center justify-center rounded-md transition",
                          view === id
                            ? "bg-gradient-brand text-white shadow-glow"
                            : "text-muted-foreground hover:text-foreground hover:bg-secondary",
                        )}
                      >
                        <Icon className="h-4 w-4" />
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {isLoading ? (
                <div className="rounded-2xl border border-border bg-card/40 p-10 text-center text-sm text-muted-foreground">
                  Loading products…
                </div>
              ) : filtered.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-border bg-card/40 p-10 text-center text-sm text-muted-foreground">
                  No products in this category yet.
                </div>
              ) : view === "list" ? (
                <div className="flex flex-col gap-3">
                  {filtered.map((it) => (
                    <ItemRow key={it.id} item={it} categoryName={activeCategory?.name} onQuickView={setQuickView} />
                  ))}
                </div>
              ) : view === "thumbnail" ? (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                  {filtered.map((it) => (
                    <ItemThumb key={it.id} item={it} onQuickView={setQuickView} />
                  ))}
                </div>
              ) : (
                <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {filtered.map((it) => (
                    <ItemCard key={it.id} item={it} categoryName={activeCategory?.name} onQuickView={setQuickView} />
                  ))}
                </div>
              )}
            </div>

          </div>
        </div>
      </section>
      <CTABanner />
      <QuickViewDialog item={quickView} onClose={() => setQuickView(null)} />
    </PageShell>
  );
}

function ItemCard({ item, categoryName, onQuickView }: { item: DigitalItem; categoryName?: string; onQuickView?: (i: DigitalItem) => void }) {
  const tracked = item.stock_tracked;
  const stock = item.stock_qty ?? 0;
  const outOfStock = tracked && stock <= 0;
  const lowStock = tracked && stock > 0 && stock <= 10;

  const handleAdd = () => {
    if (outOfStock) return;
    cartStore.add({
      id: `digital:${item.id}`,
      name: item.name,
      price: (item.price_cents || 0) / 100,
      meta: item.price_unit ?? undefined,
      href: "/marketplace",
    });
    toast.success(`${item.name} added to cart`);
  };

  const rating = Number(item.rating_avg) || 0;
  const fullStars = Math.round(rating);

  return (
    <div className="group relative flex flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-card transition hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-glow">
      <button
        type="button"
        onClick={() => onQuickView?.(item)}
        aria-label={`Quick view ${item.name}`}
        className="relative aspect-[16/10] w-full overflow-hidden bg-gradient-to-br from-violet-500/20 via-sky-500/20 to-emerald-500/20 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        {item.image_url ? (
          <img
            src={item.image_url}
            alt={item.name}
            loading="lazy"
            className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-muted-foreground/60">
            <Sparkles className="h-8 w-8" />
          </div>
        )}
        {(item.badge || categoryName) && (
          <span className="absolute left-3 top-3 rounded-full bg-background/80 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-foreground backdrop-blur">
            {item.badge || categoryName}
          </span>
        )}
        <div className="absolute right-3 top-3 flex flex-col items-end gap-1">
          {item.featured && (
            <span className="inline-flex items-center gap-1 rounded-full bg-gradient-brand px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-white shadow-glow">
              <Star className="h-3 w-3" /> Featured
            </span>
          )}
          {item.is_best_seller && (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-500 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-white shadow">
              🔥 Best Seller
            </span>
          )}
          {item.is_top && (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-white shadow">
              ⭐ Top Pick
            </span>
          )}
        </div>
        {outOfStock && (
          <span className="absolute inset-x-0 bottom-0 bg-destructive/90 px-3 py-1 text-center text-[11px] font-bold uppercase tracking-widest text-white">
            Out of stock
          </span>
        )}
        <span className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center justify-center gap-1.5 bg-gradient-to-t from-black/60 to-transparent px-3 py-2 text-[11px] font-semibold uppercase tracking-widest text-white opacity-0 transition group-hover:opacity-100">
          <Eye className="h-3.5 w-3.5" /> Quick view
        </span>
      </button>
      <div className="flex flex-1 flex-col p-4">
        <h3 className="line-clamp-2 text-base font-semibold leading-tight">{item.name}</h3>
        {item.short_description && (
          <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{item.short_description}</p>
        )}
        <div className="mt-2 flex items-center justify-between gap-2 text-xs">
          <div className="flex items-center gap-1" aria-label={`Rating ${rating} out of 5`}>
            <div className="flex">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star
                  key={i}
                  className={cn(
                    "h-3.5 w-3.5",
                    i < fullStars ? "fill-amber-400 text-amber-400" : "text-muted-foreground/30",
                  )}
                />
              ))}
            </div>
            <span className="font-semibold text-foreground">{rating.toFixed(1)}</span>
            <span className="text-muted-foreground">({item.rating_count})</span>
          </div>
          {tracked && (
            <span
              className={cn(
                "rounded-full px-2 py-0.5 font-semibold",
                outOfStock
                  ? "bg-destructive/10 text-destructive"
                  : lowStock
                    ? "bg-amber-500/10 text-amber-600"
                    : "bg-emerald-500/10 text-emerald-600",
              )}
            >
              {outOfStock ? "Out of stock" : lowStock ? `Only ${stock} left` : `${stock} in stock`}
            </span>
          )}
        </div>
        <div className="relative z-20 mt-auto flex items-center justify-between gap-2 pt-4">
          <span className="text-sm font-bold text-gradient">{formatItemPrice(item)}</span>
          <Button size="sm" onClick={handleAdd} disabled={outOfStock} className="shrink-0">
            <ShoppingCart className="mr-1 h-4 w-4" /> {outOfStock ? "Sold out" : "Add"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ItemRow({ item, categoryName, onQuickView }: { item: DigitalItem; categoryName?: string; onQuickView?: (i: DigitalItem) => void }) {
  const tracked = item.stock_tracked;
  const stock = item.stock_qty ?? 0;
  const outOfStock = tracked && stock <= 0;
  const rating = Number(item.rating_avg) || 0;
  const fullStars = Math.round(rating);

  const handleAdd = () => {
    if (outOfStock) return;
    cartStore.add({
      id: `digital:${item.id}`,
      name: item.name,
      price: (item.price_cents || 0) / 100,
      meta: item.price_unit ?? undefined,
      href: "/marketplace",
    });
    toast.success(`${item.name} added to cart`);
  };

  return (
    <div className="group relative flex gap-4 overflow-hidden rounded-2xl border border-border bg-card p-3 shadow-card transition hover:border-primary/40 hover:shadow-glow">
      <button
        type="button"
        onClick={() => onQuickView?.(item)}
        aria-label={`Quick view ${item.name}`}
        className="relative aspect-[16/10] w-40 shrink-0 overflow-hidden rounded-xl bg-gradient-to-br from-violet-500/20 via-sky-500/20 to-emerald-500/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary sm:w-56"
      >
        {item.image_url ? (
          <img src={item.image_url} alt={item.name} loading="lazy" className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]" />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-muted-foreground/60">
            <Sparkles className="h-6 w-6" />
          </div>
        )}
        <div className="absolute left-2 top-2 flex flex-col gap-1">
          {item.featured && (
            <span className="inline-flex items-center gap-1 rounded-full bg-gradient-brand px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-white shadow-glow">
              <Star className="h-3 w-3" /> Featured
            </span>
          )}
          {item.is_best_seller && (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-white">🔥 Best Seller</span>
          )}
          {item.is_top && (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-white">⭐ Top</span>
          )}
        </div>
        <span className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center justify-center gap-1 bg-gradient-to-t from-black/60 to-transparent py-1 text-[10px] font-semibold uppercase tracking-widest text-white opacity-0 transition group-hover:opacity-100">
          <Eye className="h-3 w-3" /> Quick view
        </span>
      </button>
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            {(item.badge || categoryName) && (
              <span className="mb-1 inline-block rounded-full bg-secondary px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                {item.badge || categoryName}
              </span>
            )}
            <h3 className="truncate text-base font-semibold leading-tight">{item.name}</h3>
            {item.short_description && (
              <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{item.short_description}</p>
            )}
          </div>
          <span className="shrink-0 text-base font-bold text-gradient">{formatItemPrice(item)}</span>
        </div>
        <div className="relative z-20 mt-auto flex items-center justify-between gap-2 pt-3">
          <div className="flex items-center gap-1 text-xs">
            <div className="flex">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star key={i} className={cn("h-3.5 w-3.5", i < fullStars ? "fill-amber-400 text-amber-400" : "text-muted-foreground/30")} />
              ))}
            </div>
            <span className="font-semibold text-foreground">{rating.toFixed(1)}</span>
            <span className="text-muted-foreground">({item.rating_count})</span>
            {tracked && (
              <span className={cn("ml-2 rounded-full px-2 py-0.5 font-semibold", outOfStock ? "bg-destructive/10 text-destructive" : stock <= 10 ? "bg-amber-500/10 text-amber-600" : "bg-emerald-500/10 text-emerald-600")}>
                {outOfStock ? "Out of stock" : stock <= 10 ? `Only ${stock} left` : `${stock} in stock`}
              </span>
            )}
          </div>
          <Button size="sm" onClick={handleAdd} disabled={outOfStock} className="shrink-0">
            <ShoppingCart className="mr-1 h-4 w-4" /> {outOfStock ? "Sold out" : "Add"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ItemThumb({ item, onQuickView }: { item: DigitalItem; onQuickView?: (i: DigitalItem) => void }) {
  const tracked = item.stock_tracked;
  const stock = item.stock_qty ?? 0;
  const outOfStock = tracked && stock <= 0;
  const handleAdd = () => {
    if (outOfStock) return;
    cartStore.add({
      id: `digital:${item.id}`,
      name: item.name,
      price: (item.price_cents || 0) / 100,
      meta: item.price_unit ?? undefined,
      href: "/marketplace",
    });
    toast.success(`${item.name} added to cart`);
  };
  return (
    <div className="group relative flex flex-col overflow-hidden rounded-xl border border-border bg-card shadow-card transition hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-glow">
      <button
        type="button"
        onClick={() => onQuickView?.(item)}
        aria-label={`Quick view ${item.name}`}
        className="relative aspect-square w-full overflow-hidden bg-gradient-to-br from-violet-500/20 via-sky-500/20 to-emerald-500/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        {item.image_url ? (
          <img src={item.image_url} alt={item.name} loading="lazy" className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.05]" />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-muted-foreground/60">
            <Sparkles className="h-6 w-6" />
          </div>
        )}
        <div className="absolute left-1.5 top-1.5 flex flex-col gap-0.5">
          {item.featured && <span className="rounded bg-gradient-brand px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white">Featured</span>}
          {item.is_best_seller && <span className="rounded bg-amber-500 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white">Best</span>}
          {item.is_top && <span className="rounded bg-emerald-500 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white">Top</span>}
        </div>
        {outOfStock && (
          <span className="absolute inset-x-0 bottom-0 bg-destructive/90 px-2 py-0.5 text-center text-[10px] font-bold uppercase tracking-widest text-white">
            Sold out
          </span>
        )}
        <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/40 text-white opacity-0 transition group-hover:opacity-100">
          <Eye className="h-5 w-5" />
        </span>
      </button>
      <div className="flex flex-1 flex-col gap-1 p-2.5">
        <h3 className="line-clamp-2 text-xs font-semibold leading-tight">{item.name}</h3>
        <div className="relative z-20 mt-auto flex items-center justify-between gap-1 pt-1">
          <span className="text-xs font-bold text-gradient">{formatItemPrice(item)}</span>
          <Button size="sm" variant="ghost" onClick={handleAdd} disabled={outOfStock} className="h-7 px-2">
            <ShoppingCart className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function QuickViewDialog({ item, onClose }: { item: DigitalItem | null; onClose: () => void }) {
  const open = !!item;
  const outOfStock = !!item && item.stock_tracked && (item.stock_qty ?? 0) <= 0;
  const rating = item ? Number(item.rating_avg) || 0 : 0;
  const fullStars = Math.round(rating);
  const handleAdd = () => {
    if (!item || outOfStock) return;
    cartStore.add({
      id: `digital:${item.id}`,
      name: item.name,
      price: (item.price_cents || 0) / 100,
      meta: item.price_unit ?? undefined,
      href: "/marketplace",
    });
    toast.success(`${item.name} added to cart`);
    onClose();
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl overflow-hidden p-0">
        {item && (
          <div className="grid gap-0 sm:grid-cols-2">
            <div className="relative aspect-square bg-gradient-to-br from-violet-500/20 via-sky-500/20 to-emerald-500/20 sm:aspect-auto">
              {item.image_url ? (
                <img src={item.image_url} alt={item.name} className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-muted-foreground/60">
                  <Sparkles className="h-10 w-10" />
                </div>
              )}
              <div className="absolute left-3 top-3 flex flex-col gap-1">
                {item.featured && <span className="rounded-full bg-gradient-brand px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-white shadow-glow">Featured</span>}
                {item.is_best_seller && <span className="rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-white">Best Seller</span>}
                {item.is_top && <span className="rounded-full bg-emerald-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-white">Top Pick</span>}
              </div>
            </div>
            <div className="flex flex-col p-5">
              <DialogHeader className="text-left">
                <DialogTitle className="text-lg leading-tight">{item.name}</DialogTitle>
                {item.short_description && (
                  <DialogDescription className="text-sm">{item.short_description}</DialogDescription>
                )}
              </DialogHeader>
              <div className="mt-3 flex items-center gap-2 text-xs">
                <div className="flex">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <Star key={i} className={cn("h-3.5 w-3.5", i < fullStars ? "fill-amber-400 text-amber-400" : "text-muted-foreground/30")} />
                  ))}
                </div>
                <span className="font-semibold text-foreground">{rating.toFixed(1)}</span>
                <span className="text-muted-foreground">({item.rating_count} reviews)</span>
              </div>
              {item.description && (
                <p className="mt-3 max-h-40 overflow-auto whitespace-pre-line text-sm text-muted-foreground">
                  {item.description}
                </p>
              )}
              <div className="mt-auto flex items-center justify-between gap-3 pt-5">
                <span className="text-xl font-bold text-gradient">{formatItemPrice(item)}</span>
                <Button onClick={handleAdd} disabled={outOfStock}>
                  <ShoppingCart className="mr-1 h-4 w-4" /> {outOfStock ? "Sold out" : "Add to cart"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
